import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import referralService from '../../services/referralService';

interface ReferralProgram {
  id: string;
  referralCode: string;
  referrer: {
    id: string;
    displayName: string;
    email: string;
  };
  referee?: {
    id: string;
    displayName: string;
    email: string;
  };
  status: 'PENDING' | 'ACTIVE' | 'CONVERTED' | 'EXPIRED';
  commissionRate: number;
  commissionEarned: number;
  commissionPaid: number;
  commissionPending: number;
  totalReferrals: number;
  successfulReferrals: number;
  clickCount: number;
  conversionRate: number;
  createdAt: string;
  convertedAt?: string;
  paidAt?: string;
}

interface ReferralStats {
  totalPrograms: number;
  activePrograms: number;
  totalReferrals: number;
  successfulConversions: number;
  totalCommissionEarned: number;
  totalCommissionPaid: number;
  totalCommissionPending: number;
  averageConversionRate: number;
  totalClicks: number;
}

interface ReferralActivity {
  id: string;
  type: 'CLICK' | 'SIGNUP' | 'SUBSCRIPTION' | 'COMMISSION_EARNED' | 'COMMISSION_PAID';
  referralCode: string;
  referrer: string;
  referee?: string;
  amount?: number;
  timestamp: string;
  metadata?: Record<string, any>;
}

const AdminReferralProgramsPage: React.FC = () => {
  const [programs, setPrograms] = useState<ReferralProgram[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<ReferralProgram[]>([]);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [activities, setActivities] = useState<ReferralActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreatorId] = useState<string>('creator-1'); // Default to first creator
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showPayCommissionModal, setShowPayCommissionModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<ReferralProgram | null>(null);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    creatorId: '',
    commissionRate: 10,
  });
  
  const [payForm, setPayForm] = useState({
    referralId: '',
    amount: 0,
  });

  useEffect(() => {
    if (selectedCreatorId) {
      loadData();
    }
  }, [selectedCreatorId, currentPage, statusFilter]);

  useEffect(() => {
    filterPrograms();
  }, [programs, searchQuery, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load referral programs - service returns array directly
      const programsData = await referralService.getReferrals(selectedCreatorId);
      
      // Map the service data to our component interface
      const mappedPrograms = programsData.map((ref: any) => ({
        id: ref.id,
        referralCode: ref.referralCode,
        referrer: {
          id: ref.referrer?.id || ref.referrerId,
          displayName: ref.referrer?.username || 'Unknown',
          email: ref.referrer?.email || '',
        },
        referee: ref.referred ? {
          id: ref.referred.id,
          displayName: ref.referred.username,
          email: ref.referred.email,
        } : undefined,
        status: ref.status,
        commissionRate: ref.commissionRate || 10,
        commissionEarned: ref.commission || 0,
        commissionPaid: ref.status === 'PAID' ? ref.commission : 0,
        commissionPending: ref.status !== 'PAID' ? ref.commission : 0,
        totalReferrals: 1,
        successfulReferrals: ref.status === 'QUALIFIED' || ref.status === 'PAID' ? 1 : 0,
        clickCount: 0,
        conversionRate: ref.status === 'QUALIFIED' || ref.status === 'PAID' ? 100 : 0,
        createdAt: ref.createdAt,
        convertedAt: ref.qualifiedDate,
        paidAt: ref.paidDate,
      }));
      
      setPrograms(mappedPrograms);
      setTotalPages(Math.ceil(mappedPrograms.length / itemsPerPage));
      
      // Load stats
      const statsData = await referralService.getStats(selectedCreatorId);
      const mappedStats = {
        totalPrograms: statsData.total,
        activePrograms: statsData.active,
        totalReferrals: statsData.total,
        successfulConversions: statsData.qualified,
        totalCommissionEarned: statsData.totalCommission,
        totalCommissionPaid: statsData.totalCommission, // Assuming all paid
        totalCommissionPending: 0,
        averageConversionRate: statsData.conversionRate,
        totalClicks: 0,
      };
      setStats(mappedStats);
      
    } catch (error) {
      console.error('Error loading referral programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPrograms = () => {
    let filtered = [...programs];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(program =>
        program.referralCode.toLowerCase().includes(query) ||
        program.referrer.displayName.toLowerCase().includes(query) ||
        program.referrer.email.toLowerCase().includes(query) ||
        (program.referee && (
          program.referee.displayName.toLowerCase().includes(query) ||
          program.referee.email.toLowerCase().includes(query)
        ))
      );
    }
    
    setFilteredPrograms(filtered);
  };

  const handleCreateProgram = async () => {
    try {
      // Service expects: referrerId, referredId, referralCode
      const code = 'REF' + Date.now();
      await referralService.createReferral(createForm.creatorId, '', code);
      setShowCreateModal(false);
      setCreateForm({ creatorId: '', commissionRate: 10 });
      loadData();
    } catch (error) {
      console.error('Error creating referral program:', error);
      alert('Failed to create referral program');
    }
  };

  const handlePayCommission = async () => {
    try {
      // markAsPaid only takes referralId
      await referralService.markAsPaid(payForm.referralId);
      setShowPayCommissionModal(false);
      setPayForm({ referralId: '', amount: 0 });
      setSelectedProgram(null);
      loadData();
    } catch (error) {
      console.error('Error paying commission:', error);
      alert('Failed to process commission payment');
    }
  };

  const handleViewActivity = async (program: ReferralProgram) => {
    try {
      setSelectedProgram(program);
      // Service doesn't support activity tracking yet, use empty array
      setActivities([]);
      setShowActivityModal(true);
    } catch (error) {
      console.error('Error loading activity:', error);
    }
  };

  const handleCalculateCommission = async (program: ReferralProgram) => {
    try {
      // calculateCommission returns a Referral object with updated commission
      const result = await referralService.calculateCommission(
        program.id,
        program.commissionEarned
      );
      alert(`Commission calculated: $${result.commission.toFixed(2)}`);
      loadData();
    } catch (error) {
      console.error('Error calculating commission:', error);
      alert('Failed to calculate commission');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800';
      case 'CONVERTED':
        return 'bg-green-100 text-green-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedCreatorId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Creator Selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select a creator to view their referral programs.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading referral programs...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Referral Programs Manager</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage creator referral programs, track commissions, and monitor performance.
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Programs</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalPrograms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Programs</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.activePrograms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Earned</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${stats.totalCommissionEarned.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.averageConversionRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-indigo-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Referrals</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalReferrals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Conversions</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.successfulConversions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Commission Paid</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${stats.totalCommissionPaid.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Commission Pending</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${stats.totalCommissionPending.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Search by code, referrer, or referee..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <select
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="CONVERTED">Converted</option>
              <option value="EXPIRED">Expired</option>
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Program
            </button>
          </div>
        </div>
      </div>

      {/* Referral Programs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Referral Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Referrer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Referee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Referrals
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Commission Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Earned
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pending
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPrograms.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No referral programs found</p>
                </td>
              </tr>
            ) : (
              filteredPrograms.map((program) => (
                <tr key={program.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {program.referralCode}
                      </code>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {program.referrer.displayName}
                    </div>
                    <div className="text-sm text-gray-500">{program.referrer.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {program.referee ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">
                          {program.referee.displayName}
                        </div>
                        <div className="text-sm text-gray-500">{program.referee.email}</div>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">No referee yet</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(
                        program.status
                      )}`}
                    >
                      {program.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {program.successfulReferrals}/{program.totalReferrals}
                    </div>
                    <div className="text-xs text-gray-500">
                      {program.conversionRate.toFixed(1)}% conversion
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {program.commissionRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    ${program.commissionEarned.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                    ${program.commissionPending.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewActivity(program)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View Activity"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleCalculateCommission(program)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Calculate Commission"
                      >
                        <ArrowPathIcon className="h-5 w-5" />
                      </button>
                      {program.commissionPending > 0 && (
                        <button
                          onClick={() => {
                            setSelectedProgram(program);
                            setPayForm({
                              referralId: program.id,
                              amount: program.commissionPending,
                            });
                            setShowPayCommissionModal(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Pay Commission"
                        >
                          <CurrencyDollarIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Program Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Create Referral Program
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Creator ID</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.creatorId}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, creatorId: e.target.value })
                      }
                      placeholder="Enter creator ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.commissionRate}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, commissionRate: Number(e.target.value) })
                      }
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                  onClick={handleCreateProgram}
                >
                  Create
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pay Commission Modal */}
      {showPayCommissionModal && selectedProgram && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Pay Commission
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">
                      Referral Code: <code className="font-mono bg-gray-100 px-2 py-1 rounded">{selectedProgram.referralCode}</code>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Referrer: {selectedProgram.referrer.displayName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Commission Amount ($)
                    </label>
                    <input
                      type="number"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={payForm.amount}
                      onChange={(e) =>
                        setPayForm({ ...payForm, amount: Number(e.target.value) })
                      }
                      min="0"
                      step="0.01"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Pending: ${selectedProgram.commissionPending.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
                  onClick={handlePayCommission}
                >
                  Pay Commission
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => {
                    setShowPayCommissionModal(false);
                    setSelectedProgram(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {showActivityModal && selectedProgram && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Referral Activity - {selectedProgram.referralCode}
                </h3>
                <div className="mt-4">
                  {activities.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No activity yet</p>
                  ) : (
                    <div className="space-y-4">
                      {activities.map((activity) => (
                        <div key={activity.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {activity.type}
                            </span>
                            <span className="text-sm text-gray-500">{activity.timestamp}</span>
                          </div>
                          {activity.amount && (
                            <p className="text-sm text-gray-600">Amount: ${activity.amount}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => {
                    setShowActivityModal(false);
                    setSelectedProgram(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferralProgramsPage;
